// Файл navigation_settings_store.js

/**
 * Хранит персональные настройки навигации для TabWidget.
 * Нормализует значения, уведомляет подписчиков и исключает повторную
 * загрузку уже полученных настроек.
 */

import { apiService } from '../../services/api';
import {
    request_interface_profile,
} from '../../services/api_parts/interface_profile_transport';

import {
    create_default_navigation_settings,
} from './navigation_setting_definitions';



const listeners = new Set();
let load_promise = null;
let is_loaded = false;
let current_settings = create_default_navigation_settings();

const clone_settings = (value) => JSON.parse(JSON.stringify(value));

const normalize_navigation_settings = (value) => {
    const defaults = create_default_navigation_settings();
    const source = value && typeof value === 'object' ? value : {};

    Object.entries(defaults).forEach(([group_key, group]) => {
        const source_group = source[group_key];

        if (!source_group || typeof source_group !== 'object') {
            return;
        }

        Object.keys(group).forEach((section_key) => {
            defaults[group_key][section_key] = Boolean(
                source_group[section_key]
            );
        });

        if (
            group_key === 'contextual_navigation'
            && !Object.prototype.hasOwnProperty.call(
                source_group,
                'taxes_and_tax_reporting',
            )
        ) {
            defaults[group_key].taxes_and_tax_reporting = Boolean(
                source_group.houses_and_communal_service
            );
        }

        if (
            group_key === 'contextual_navigation'
            && !Object.prototype.hasOwnProperty.call(
                source_group,
                'accounting_and_reporting',
            )
        ) {
            defaults[group_key].accounting_and_reporting = Boolean(
                source_group.houses_and_communal_service
            );
        }

        if (
            group_key === 'contextual_navigation'
            && !Object.prototype.hasOwnProperty.call(
                source_group,
                'personnel_and_salary',
            )
        ) {
            const legacy_sections = [
                'houses_and_communal_service',
                'document_management',
                'taxes_and_tax_reporting',
                'app_settings',
            ].filter((section_key) => (
                Object.prototype.hasOwnProperty.call(
                    source_group,
                    section_key,
                )
            ));

            defaults[group_key].personnel_and_salary = Boolean(
                legacy_sections.length
                && legacy_sections.every((section_key) => (
                    Boolean(source_group[section_key])
                ))
            );
        }
    });

    return defaults;
};

const emit_change = () => {
    const snapshot = clone_settings(current_settings);
    listeners.forEach((listener) => listener(snapshot));
};

const update_settings = (value) => {
    current_settings = normalize_navigation_settings(value);
    emit_change();

    return clone_settings(current_settings);
};

export const get_navigation_settings = () => (
    clone_settings(current_settings)
);

export const navigation_setting_enabled = (group_key, section_key) => (
    Boolean(current_settings?.[group_key]?.[section_key])
);

export const install_navigation_settings = (settings) => {
    is_loaded = true;

    return update_settings(settings);
};

export const ensure_navigation_settings_loaded = () => {
    if (is_loaded) {
        return Promise.resolve(get_navigation_settings());
    }

    if (load_promise) {
        return load_promise;
    }

    load_promise = request_interface_profile(
        apiService,
    ).then((response) => (
        install_navigation_settings(
            response?.navigation_settings || {}
        )
    )).catch(() => {
        is_loaded = true;

        return get_navigation_settings();

    }).finally(() => {
        load_promise = null;
    });

    return load_promise;
};

export const save_navigation_settings = async (settings) => {
    const normalized = normalize_navigation_settings(settings);
    const response = await apiService.df({
        table_name: 'interface_customization_settings',
        params: { action: 'save_navigation' },
        table_data: normalized,
    });

    return {
        response,
        settings: update_settings(
            response?.navigation_settings || normalized
        ),
    };
};

export const subscribe_navigation_settings = (listener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};
